import {cva} from 'platform/shared';

import {
  ComponentType,
  createElement,
  CSSProperties,
  KeyboardEventHandler,
  MouseEventHandler,
  Suspense,
  SVGProps,
} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {ThemeColorPath, ThemeIconKey} from '../../utils/foundationTheme';
import {getColorByPath} from '../../utils/getColorByPath';
import {getSize} from '../../utils/getSize';
import {getThemeValue} from '../../utils/getThemeValue';
import {themeIcons} from '../../utils/icons/themeIcons';
import {IconErrorBoundary} from './IconErrorBoundary';

const FILLED_ICON_CLASS = `
  [&_svg:not([data-iconkey^='flags/']):not([data-iconkey^='flagsCurrencies/']):not([data-iconkey^='files_compact/']):not([data-iconkey^='files_default/']):not([data-iconkey^='condition/']):not([data-iconkey^='automotive/'])_path]:fill-current
`;

const iconVariants = cva(
  'leading-[0] transition-[box-shadow] duration-[250ms] ease-in-out rounded-[var(--radius-xs)] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_currentColor]',
  {
    variants: {
      isFilled: {
        true: FILLED_ICON_CLASS,
        false: '',
      },
    },
  }
);

export type IconRegistry = Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

type IconSource = ComponentType<SVGProps<SVGSVGElement>> | string;
type IconSvgProps = SVGProps<SVGSVGElement> & {'data-iconkey'?: string};

function isIconComponent(
  iconSource: IconSource | undefined
): iconSource is ComponentType<SVGProps<SVGSVGElement>> {
  return !isNil(iconSource) && typeof iconSource !== 'string';
}

export interface IconProps<TIconKey extends string = ThemeIconKey> extends TestIdProps {
  value: TIconKey | undefined;
  // tw-only extension used by mira; not part of platform/foundation parity API
  iconRegistry?: IconRegistry;
  color?: ThemeColorPath;
  cursor?: CSSProperties['cursor'];
  size?: Integer | ValueByDevice<Integer>;
  isNotFilled?: boolean;
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export function Icon<TIconKey extends string = ThemeIconKey>(props: IconProps<TIconKey>) {
  const theme = useFoundationTheme();
  const size = useResponsivePropValue(props.size ?? 6);

  if (!props.value) {
    return null;
  }

  const iconRegistry = props.iconRegistry ?? theme.icons;
  const FallbackIconComponent =
    [
      getThemeValue<IconSource>(iconRegistry, 'fallback'),
      getThemeValue<IconSource>(theme.icons, 'fallback'),
      themeIcons.fallback,
    ].find(isIconComponent) ?? themeIcons.fallback;
  const IconComponent = [
    getThemeValue<IconSource>(iconRegistry, props.value),
    getThemeValue<IconSource>(theme.icons, props.value),
    getThemeValue<IconSource>(themeIcons, props.value),
  ].find(isIconComponent);
  const iconSize = getSize(size);
  const iconProps: SVGProps<SVGSVGElement> = {
    style: {
      height: iconSize,
      width: iconSize,
    },
  };
  const renderedIconProps: IconSvgProps = {...iconProps, 'data-iconkey': props.value};
  const fallbackIcon = createElement(FallbackIconComponent, iconProps);
  const style: CSSProperties = {
    color: props.color ? getColorByPath(props.color, theme) : 'currentColor',
    cursor: props.cursor ?? (!isNil(props.onClick) ? 'pointer' : undefined),
  };

  return (
    <div
      role="img"
      className={iconVariants({isFilled: !props.isNotFilled})}
      style={style}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      tabIndex={props.tabIndex}
      data-iconkey={props.value}
      data-testid={suffixTestId('icon', props)}
    >
      <IconErrorBoundary icon={props.value} fallback={fallbackIcon}>
        <Suspense fallback={fallbackIcon}>
          {isNil(IconComponent) ? null : createElement(IconComponent, renderedIconProps)}
        </Suspense>
      </IconErrorBoundary>
    </div>
  );
}
